import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, ShieldCheck } from 'lucide-react';
import dayjs from 'dayjs';
import ContactAddressForm from 'components/forms/employees/ContactAddressForm';
import CredentialsRoleForm from 'components/forms/employees/CredentialsRoleForm';
import PersonalDetailsForm from 'components/forms/employees/PersonalDetailsForm';
import Loader from 'components/AntEnhancement/Loader';
import localeTranslator from 'library/Hocs/LocalTranslator';
import services from 'services';
import '../NewEmployee/style.css';
var UpdateEmployee = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var id = useParams().id;
    var employeeId = Number(id);
    var _a = useState(false), disabled = _a[0], setDisabled = _a[1];
    var form = Form.useForm()[0];
    var _b = useState(null), user = _b[0], setUser = _b[1];
    var _c = useState(false), prefilled = _c[0], setPrefilled = _c[1];
    var employeeService = services.employeeService, jobTitleService = services.jobTitleService;
    var _d = employeeService.useGetEmployee(employeeId), employee = _d.data, isLoading = _d.isLoading;
    var jobTitles = jobTitleService.useJobTitles().data;
    var _e = employeeService.useUpdateEmployee(), updateEmployee = _e.mutate, requestStatus = _e.status;
    useEffect(function () {
        setUser(null);
        setPrefilled(false);
        form.resetFields();
    }, [employeeId, form]);
    useEffect(function () {
        if (!employee || user)
            return;
        setUser(employee.getUser());
    }, [employee, user]);
    useEffect(function () {
        if (!employee || !user || prefilled)
            return;
        var birth = user.birthday ? dayjs(user.getBirthDay()) : null;
        var jobTitleId = employee.getJobTitleId();
        var gender = (user.getGender() || '').toLowerCase();
        var maritalStatus = (employee.getMartialStatus() || '').toLowerCase();
        var homePhone = user.getHomePhone();
        form.setFieldsValue({
            fullName: user.getName() || undefined,
            nationalId: employee.getNationalId() || undefined,
            birthdate: (birth === null || birth === void 0 ? void 0 : birth.isValid()) ? birth : undefined,
            gender: gender || undefined,
            maritalStatus: maritalStatus || undefined,
            mobile: user.getMobile() || undefined,
            secondaryPhone: homePhone && homePhone !== '-' ? homePhone : undefined,
            email: user.getEmail() || undefined,
            address: user.getAddress() || undefined,
            jobTitle: jobTitleId > 0 ? jobTitleId : undefined
        });
        setPrefilled(true);
    }, [employee, user, form, prefilled]);
    var syncModelsFromForm = function () {
        var _a;
        if (!employee || !user)
            return;
        var values = form.getFieldsValue(true);
        if (values.fullName)
            user.setName(String(values.fullName).trim());
        if (values.gender)
            user.setGender(String(values.gender));
        if (values.email != null)
            user.setEmail(String(values.email).trim());
        if (values.mobile)
            user.setMobile(String(values.mobile).trim());
        if (values.secondaryPhone != null) {
            user.setHomePhone(String(values.secondaryPhone).trim());
        }
        if (values.address != null)
            user.setAddress(String(values.address).trim());
        if (values.password) {
            user.setPassword(String(values.password));
            user.setPasswordConfirmation(String(values.passwordConfirmation || ''));
        }
        else {
            user.setPassword('');
            user.setPasswordConfirmation('');
        }
        var birthdate = values.birthdate;
        if ((_a = birthdate === null || birthdate === void 0 ? void 0 : birthdate.isValid) === null || _a === void 0 ? void 0 : _a.call(birthdate)) {
            user.setBirthDay(birthdate.toDate());
        }
        if (values.nationalId) {
            employee.setNationalId(String(values.nationalId).trim());
        }
        if (values.maritalStatus) {
            employee.setMartialStatusId(String(values.maritalStatus));
        }
        if (values.jobTitle != null && jobTitles) {
            var jobTitle = jobTitles.find(function (item) { return item.getId() === Number(values.jobTitle); });
            if (jobTitle)
                employee.setJobTitle(jobTitle);
        }
        var salary = Number(employee.getSalary());
        if (!Number.isNaN(salary))
            employee.setSalary(Math.round(salary));
        employee.setUser(user);
    };
    var submit = function () {
        if (!employee)
            return;
        setDisabled(true);
        syncModelsFromForm();
        updateEmployee({
            employee: employee,
            successMessage: t('employeeUpdated')
        });
    };
    useEffect(function () {
        if (requestStatus === 'error') {
            setDisabled(false);
        }
    }, [requestStatus]);
    var scrollToFirstError = function () {
        form.scrollToField('fullName', { behavior: 'smooth' });
    };
    var goBack = function () { return navigate("/employees/".concat(employeeId)); };
    return (_jsx(Loader, { renderSpinner: isLoading && !employee, children: _jsxs("div", { className: "employee-form-page", children: [_jsxs("header", { className: "employee-form-page__header", children: [_jsxs("nav", { className: "employee-form-page__breadcrumb", "aria-label": "Breadcrumb", children: [_jsx("span", { className: "employee-form-page__crumb employee-form-page__crumb--muted", children: t('breadcrumbClinic') }), _jsx(ChevronRight, { size: 13, strokeWidth: 2, "aria-hidden": true }), _jsx(Link, { to: "/employees", className: "employee-form-page__crumb", children: t('breadcrumbEmployees') }), _jsx(ChevronRight, { size: 13, strokeWidth: 2, "aria-hidden": true }), _jsx("span", { className: "employee-form-page__crumb employee-form-page__crumb--current", children: t('breadcrumbEdit') })] }), _jsxs("div", { className: "employee-form-page__title-row", children: [_jsx("button", { type: "button", className: "employee-form-page__back", onClick: goBack, "aria-label": t('common:buttons.cancel'), children: _jsx(ArrowLeft, { size: 18, strokeWidth: 2 }) }), _jsx("h1", { className: "employee-form-page__title", children: t('editTitle') })] })] }), _jsxs("div", { className: "employee-form-page__canvas", children: [employee && user ? (_jsxs(Form, { className: "employee-form", layout: "vertical", form: form, onFinish: submit, onFinishFailed: scrollToFirstError, requiredMark: false, children: [_jsx(PersonalDetailsForm, { employee: employee, user: user }), _jsx(ContactAddressForm, { user: user }), jobTitles ? (_jsx(CredentialsRoleForm, { employee: employee, user: user, jobTitles: jobTitles, requirePassword: false })) : null, _jsxs("div", { className: "employee-form-page__footer", children: [_jsxs("p", { className: "employee-form-page__hint", children: [_jsx("span", { className: "employee-form-page__hint-dot", "aria-hidden": true, children: "\u2022" }), _jsxs("span", { children: [t('requiredHintBefore'), _jsx("span", { className: "employee-form__req", children: "*" }), t('requiredHintAfter')] })] }), _jsxs("div", { className: "employee-form-page__footer-actions", children: [_jsx("button", { type: "button", className: "employee-form-page__btn employee-form-page__btn--ghost", onClick: goBack, children: t('common:buttons.cancel') }), _jsxs("button", { type: "submit", className: "employee-form-page__btn employee-form-page__btn--primary", disabled: disabled, children: [_jsx(Check, { size: 16, strokeWidth: 2 }), _jsx("span", { children: t('save') })] })] })] })] })) : null, _jsxs("div", { className: "employee-form-page__meta", children: [_jsxs("div", { className: "employee-form-page__meta-left", children: [_jsx(ShieldCheck, { size: 14, strokeWidth: 2, "aria-hidden": true }), _jsx("span", { children: t('rbacNote') }), _jsx("span", { className: "employee-form-page__meta-sep", "aria-hidden": true, children: "\u2022" }), _jsx("span", { children: t('encryptionNote') })] }), _jsx("span", { className: "employee-form-page__meta-id", children: t('facilityId') })] })] })] }) }));
};
export default localeTranslator(UpdateEmployee, 'employeeForm');
